import React, { useState } from "react";

import { process, State } from "@progress/kendo-data-query";
import { Button } from "@progress/kendo-react-buttons";
import { Grid, GridColumn, GridDataStateChangeEvent, GridToolbar } from "@progress/kendo-react-grid";
import { GridRowDoubleClickEvent } from "@progress/kendo-react-grid/dist/npm/interfaces/events";
import remove from "lodash/remove";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Dispatch } from "redux";

import { IDossierSectionProps } from "..";
import { derender, render } from "../../../../../../state/actions/renderActions";
import { IDossier, IServiceItem } from "../../../../../../utils/types/models";
import { newKey } from "../../../../../../utils/utils";
import Confirm from "../../../../../global/confirm";
import { getGridCommandColumn } from "../../../../../global/gridpanel";
import { customCell, dateCell, multilineCell, translatedCell } from "../../../../../global/gridpanel/customCells/gridCells";
import { IFunctionCommand, IRecordCommand } from "../../../../../global/gridpanel/customCells/gridCells/commandCell";
import ServiceItemEditor from "./editor";

import style from "../editor.module.scss";

const AfterSales: React.FC<IDossierSectionProps> = (props: IDossierSectionProps) => {
	const { t } = useTranslation();
	const [gridState, setGridState] = useState<State>({ sort: [{ field: "logDate", dir: "desc" }] });
	const [currentNewId, setCurrentNewId] = useState(-1);
	const dispatch: Dispatch = useDispatch();

	function showEditor(record?: IServiceItem): void {
		const key: string = newKey("ServiceItem_Editor");
		dispatch(
			render(key, ServiceItemEditor, {
				record,
				dossier: props.dossier,
				onClose: (serviceItem: IServiceItem) => {
					if (serviceItem) {
						const newDossier: IDossier = { ...props.dossier };
						if (serviceItem.id) {
							remove(newDossier.serviceItems, { id: serviceItem.id });
						} else {
							serviceItem.id = currentNewId;
							setCurrentNewId(currentNewId - 1);
						}
						newDossier.serviceItems.push(serviceItem);
						props.onChange(newDossier);
					}
					dispatch(derender(key));
				},
				readOnly: props.readOnly
			})
		);
	}

	const commands: (IFunctionCommand<IServiceItem> | IRecordCommand<IServiceItem>)[] = [
		{
			tooltip: t("edit"),
			iconClass: "las la-pencil-alt",
			recordAction: (record: IServiceItem) => {
				showEditor(record);
			}
		},
		{
			tooltip: t("remove"),
			iconClass: "las la-times",
			idAction: (id: number) => {
				const confirmKey: string = newKey("confirm");
				dispatch(
					render(confirmKey, Confirm, {
						title: t("confirm_title", { action: t("remove").toLowerCase() }),
						children: t("confirm_content", { action: t("remove").toLowerCase() }),
						onDecline: () => dispatch(derender(confirmKey)),
						onConfirm: () => {
							dispatch(derender(confirmKey));
							const newDossier: IDossier = { ...props.dossier };
							remove(newDossier.serviceItems, { id });
							props.onChange(newDossier);
						}
					})
				);
			}
		}
	];

	return (
		<div className="h-100 d-flex flex-column k-form">
			<div className="row">
				<div className="col">
					<h2 className={style.accentTitle}>{t("aftersales")}</h2>
				</div>
				<div className="col" style={{ textAlign: "right" }}>
					<h3>
						{props.dossier?.dossierNumber} - {props.dossier?.customer?.name}
					</h3>
				</div>
			</div>

			<div className="row flex-grow-1">
				<div className="col">
					<Grid
						data={process(props.dossier.serviceItems, gridState)}
						{...gridState}
						sortable
						onDataStateChange={(event: GridDataStateChangeEvent) => setGridState(event.dataState)}
						onRowDoubleClick={(event: GridRowDoubleClickEvent) => showEditor(event.dataItem)}
						className="h-100"
					>
						{!props.readOnly && (
							<GridToolbar>
								<Button primary onClick={() => showEditor()}>
									{t("add")}
								</Button>
							</GridToolbar>
						)}
						<GridColumn field="status" title={t("status")} cell={customCell(translatedCell())} />
						<GridColumn field="logger.fullName" title={t("loggedBy")} />
						<GridColumn field="logDate" title={t("date")} cell={customCell(dateCell())} />
						<GridColumn field="description" title={t("remark")} cell={customCell(multilineCell())} />
						{!props.readOnly && getGridCommandColumn(t("actions"), commands)}
					</Grid>
				</div>
			</div>
		</div>
	);
};

export default AfterSales;
